const ALERT_PRIORITIES = new Set(["routine", "important", "urgent"]);
const ALERT_SOURCES = new Set([
  "messages",
  "appointments",
  "referrals",
  "pharmacy",
  "payments",
  "system",
]);
const ALERT_AUDIENCES = new Set([
  "patient",
  "doctor",
  "pharmacy",
  "admin",
  "management",
  "staff",
  "mixed",
]);

const PRIORITY_ORDER = {
  urgent: 3,
  important: 2,
  routine: 1,
};

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

const CHANNEL_DEFAULTS = {
  routine: { in_app: true, push: false, sound: false, vibration: false },
  important: { in_app: true, push: false, sound: false, vibration: false },
  urgent: { in_app: true, push: true, sound: true, vibration: true },
};

function safeLower(value) {
  return String(value || "").trim().toLowerCase();
}

function maybeJson(value) {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeUuid(value) {
  const text = String(value || "").trim();
  return UUID_RE.test(text) ? text : "";
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function inferAlertSource(row = {}) {
  const explicit = safeLower(row?.source);
  if (ALERT_SOURCES.has(explicit)) return explicit;

  const type = safeLower(row?.type);
  const text = normalizeText(`${row?.title} ${row?.message} ${row?.type}`);

  if (type.startsWith("message_thread:")) return "messages";
  if (type.startsWith("referral_") || text.includes("referral")) return "referrals";
  if (
    type.includes("payment") ||
    text.includes("payment requested") ||
    text.includes("payment confirmed") ||
    text.includes("paid")
  ) {
    return "payments";
  }
  if (
    type.includes("prescription") ||
    type.includes("pharmacy") ||
    type.includes("fulfillment") ||
    text.includes("ready for pickup") ||
    text.includes("pharmacy")
  ) {
    return "pharmacy";
  }
  if (
    type.includes("appointment") ||
    type.includes("consultation") ||
    text.includes("appointment") ||
    text.includes("consultation")
  ) {
    return "appointments";
  }
  return "system";
}

function inferAlertPriority(row = {}) {
  const explicit = safeLower(row?.priority);
  if (explicit === "normal") return "routine";
  if (ALERT_PRIORITIES.has(explicit)) return explicit;

  const source = inferAlertSource(row);
  const type = safeLower(row?.type);
  const text = normalizeText(`${row?.title} ${row?.message} ${row?.type}`);

  if (
    source === "payments" &&
    (
      type.includes("payment_confirmed") ||
      type.includes("payment_paid") ||
      text.includes("payment confirmed") ||
      text.includes("payment received") ||
      text.includes("already paid") ||
      text.includes("payment successful")
    )
  ) {
    return "routine";
  }

  if (
    explicit === "urgent" ||
    text.includes("urgent") ||
    text.includes("critical") ||
    text.includes("emergency") ||
    type.includes("ehr_access")
  ) {
    return "urgent";
  }

  if (
    type.startsWith("referral_") ||
    type.includes("payment") ||
    type.includes("prescription") ||
    type.includes("pharmacy") ||
    type.includes("fulfillment") ||
    text.includes("ready for pickup") ||
    text.includes("payment requested") ||
    text.includes("referral") ||
    text.includes("requested an appointment") ||
    text.includes("pending for your approval") ||
    text.includes("approved") ||
    text.includes("declined") ||
    text.includes("rescheduled")
  ) {
    return "important";
  }

  return "routine";
}

function inferAlertAudience(value, fallbackAudience = "mixed") {
  const explicit = safeLower(value);
  if (ALERT_AUDIENCES.has(explicit)) return explicit;

  const fallback = safeLower(fallbackAudience);
  return ALERT_AUDIENCES.has(fallback) ? fallback : "mixed";
}

function inferAlertChannels(value, priority) {
  const defaults = CHANNEL_DEFAULTS[priority] || CHANNEL_DEFAULTS.routine;
  const raw =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : maybeJson(value);

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...defaults };
  }

  return {
    in_app:
      typeof raw.in_app === "boolean" ? raw.in_app : defaults.in_app,
    push: typeof raw.push === "boolean" ? raw.push : defaults.push,
    sound: typeof raw.sound === "boolean" ? raw.sound : defaults.sound,
    vibration:
      typeof raw.vibration === "boolean" ? raw.vibration : defaults.vibration,
  };
}

function inferGroupKey(row = {}, source, priority) {
  const explicit = firstText(row?.group_key, row?.groupKey);
  if (explicit) return explicit;

  if (priority === "urgent") return "";

  const type = safeLower(row?.type);
  const payload = maybeJson(row?.message);
  const appointmentId = normalizeUuid(
    row?.appointment_id || row?.appointmentId || payload?.appointment_id || payload?.appointmentId
  );
  const referralId = normalizeUuid(
    row?.referral_id || row?.referralId || payload?.referral_id || payload?.referralId
  );
  const conversationId = normalizeUuid(
    payload?.conversation_id || payload?.conversationId
  );

  if (source === "appointments" && appointmentId) {
    if (type.includes("reminder")) return `appointments:reminder:${appointmentId}`;
    if (type.includes("booking_request")) return `appointments:booking:${appointmentId}`;
    if (type.includes("reschedule_request")) return `appointments:reschedule:${appointmentId}`;
    if (type.includes("cancellation_request")) return `appointments:cancellation:${appointmentId}`;
  }

  if (source === "referrals" && referralId) {
    return `referrals:${referralId}`;
  }

  if (source === "messages" && conversationId) {
    return `messages:${conversationId}`;
  }

  return "";
}

export function isAlertBellEligible(alert = {}) {
  if (inferAlertSource(alert) === "messages") return false;
  return inferAlertPriority(alert) !== "routine";
}

export function isAlertHighlighted(alert = {}) {
  const priority = inferAlertPriority(alert);
  return priority === "important" || priority === "urgent";
}

export function sortAlertsByAttention(items = []) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aPriority = PRIORITY_ORDER[inferAlertPriority(a)] || 0;
    const bPriority = PRIORITY_ORDER[inferAlertPriority(b)] || 0;
    if (aPriority !== bPriority) return bPriority - aPriority;

    const aTime = new Date(a?.updated_at || a?.updatedAt || a?.created_at || a?.createdAt || 0).getTime();
    const bTime = new Date(b?.updated_at || b?.updatedAt || b?.created_at || b?.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

export function hydrateAlert(row = {}, { fallbackAudience = "mixed" } = {}) {
  const source = inferAlertSource(row);
  const priority = inferAlertPriority(row);
  const audience = inferAlertAudience(row?.audience, fallbackAudience);
  const channels = inferAlertChannels(row?.channels, priority);
  const quietByDefault =
    typeof row?.quiet_by_default === "boolean"
      ? row.quiet_by_default
      : priority !== "urgent";
  const escalationEligible =
    typeof row?.escalation_eligible === "boolean"
      ? row.escalation_eligible
      : priority !== "routine";
  const groupKey = inferGroupKey(row, source, priority);

  return {
    ...row,
    source,
    priority,
    audience,
    channels,
    quiet_by_default: quietByDefault,
    escalation_eligible: escalationEligible,
    group_key: groupKey,
    bellEligible: isAlertBellEligible({ ...row, source, priority }),
    highlighted: isAlertHighlighted({ ...row, source, priority }),
  };
}

export function hydratePharmacyAlert(item = {}) {
  return hydrateAlert(
    {
      ...item,
      source: "pharmacy",
      audience: "pharmacy",
      priority: firstText(item?.priority, item?.kind === "stock-warning" ? "important" : "important"),
      channels: item?.channels || CHANNEL_DEFAULTS.important,
      quiet_by_default:
        typeof item?.quiet_by_default === "boolean" ? item.quiet_by_default : true,
      escalation_eligible:
        typeof item?.escalation_eligible === "boolean"
          ? item.escalation_eligible
          : true,
      group_key: firstText(item?.group_key, item?.groupKey),
      updatedAt: item?.updatedAt || item?.createdAt,
      createdAt: item?.createdAt,
    },
    { fallbackAudience: "pharmacy" }
  );
}

export function groupAlertsForAttention(items = []) {
  const normalized = sortAlertsByAttention(items);
  const grouped = new Map();
  const output = [];

  normalized.forEach((item) => {
    const groupKey = firstText(item?.group_key, item?.groupKey);
    if (!groupKey || inferAlertPriority(item) === "urgent") {
      output.push(item);
      return;
    }

    const bucket = grouped.get(groupKey) || [];
    bucket.push(item);
    grouped.set(groupKey, bucket);
  });

  grouped.forEach((bucket, groupKey) => {
    const sorted = sortAlertsByAttention(bucket);
    const lead = sorted[0];
    if (sorted.length === 1) {
      output.push(lead);
      return;
    }

    output.push({
      ...lead,
      id: `group:${groupKey}`,
      grouped: true,
      groupCount: sorted.length,
      memberIds: sorted
        .map((item) => String(item?.id || "").trim())
        .filter(Boolean),
      title:
        firstText(lead?.group_title, lead?.groupTitle) || lead?.title || "Alert",
      message:
        firstText(lead?.group_message, lead?.groupMessage)?.replace(
          /\{count\}/g,
          String(sorted.length)
        ) ||
        `${sorted.length} related updates need review.`,
    });
  });

  return sortAlertsByAttention(output);
}

export function buildAlertRecord(input = {}, { fallbackAudience = "mixed" } = {}) {
  const hydrated = hydrateAlert(input, { fallbackAudience });
  return {
    title: String(hydrated?.title || "Notification").trim() || "Notification",
    message: String(hydrated?.message || "").trim(),
    type: String(hydrated?.type || "system").trim() || "system",
    priority: hydrated.priority,
    source: hydrated.source,
    audience: hydrated.audience,
    channels: hydrated.channels,
    quiet_by_default: hydrated.quiet_by_default,
    escalation_eligible: hydrated.escalation_eligible,
    group_key: hydrated.group_key || null,
  };
}
